import { Button, Toggle, CopyToClipboard } from '@/shared';
import { ResidentsErrorBoundary } from '../components';
import { useState } from 'react';
import {
  ResidentPaymentHistoryTable,
} from '../components/ResidentPaymentHistoryTable';
import {
  AddResidentModal,
  ArchieveResidentModal,
  DeleteResidentModal,
} from '../components/modals';
import {
  OptionMenu,
  type OptionMenuItem,
} from '@/shared/components/layout/OptionMenu';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useResidentDetails } from '../hooks/useResidentQueries';
import { formattedDate } from '@/shared/utils/formatDate';

// 1️⃣ Define the Permission type
type Permission = 'lateFeeApplied' | 'customCharges';

// 2️⃣ Define structure for permission items
interface PermissionItem {
  key: Permission;
  label: string;
  defaultValue: boolean;
  handler: (value: boolean) => void;
}

const ResidentDetailsPageContent = () => {
  const navigate = useNavigate();
  // Example: local state for demonstration (could also come from API)
  const [permissions, setPermissions] = useState<Record<Permission, boolean>>({
    lateFeeApplied: true,
    customCharges: false,
  });
  const [isAddResidentModalOpen, setAddResidentModalOpen] =
    useState<boolean>(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  // Custom handlers for each permission (could call APIs here)
  const handleToggle = (key: Permission) => (value: boolean) => {
    console.log(`Toggled ${key}:`, value);
    // Example: Update local state
    setPermissions((prev) => ({ ...prev, [key]: value }));

    // Example: You can make an API call here for each toggle
    // await api.updatePermission(userId, key, value);
  };

  // 3️⃣ Define permission config (label + handler)
  const permissionList: PermissionItem[] = [
    {
      key: 'lateFeeApplied',
      label: 'Late fees applied',
      defaultValue: permissions.lateFeeApplied,
      handler: handleToggle('lateFeeApplied'),
    },
    {
      key: 'customCharges',
      label: 'Custom charges',
      defaultValue: permissions.customCharges,
      handler: handleToggle('customCharges'),
    },
  ];

  const residentMenuItems: OptionMenuItem[] = [
    {
      label: 'Edit Resident Details',
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 16H3.425L13.2 6.225L11.775 4.8L2 14.575V16ZM0 18V13.75L13.2 0.575C13.4 0.391667 13.6208 0.25 13.8625 0.15C14.1042 0.05 14.3583 0 14.625 0C14.8917 0 15.15 0.05 15.4 0.15C15.65 0.25 15.8667 0.4 16.05 0.6L17.425 2C17.625 2.18333 17.7708 2.4 17.8625 2.65C17.9542 2.9 18 3.15 18 3.4C18 3.66667 17.9542 3.92083 17.8625 4.1625C17.7708 4.40417 17.625 4.625 17.425 4.825L4.25 18H0ZM12.475 5.525L11.775 4.8L13.2 6.225L12.475 5.525Z"
            fill="#474747"
          />
        </svg>
      ),
      onClick: () => {
        navigate({
          to: `/houses/residents/edit/${residentId}`,
        });
      },
    },
    // {
    //   label: 'View Payment History',
    //   icon: (
    //     <svg
    //       width="20"
    //       height="18"
    //       viewBox="0 0 20 18"
    //       fill="none"
    //       xmlns="http://www.w3.org/2000/svg"
    //     >
    //       <path
    //         d="M0.25 17.7751V15.9001H19.75V17.7751H0.25ZM1.225 14.9001V8.2251H4.1V14.9001H1.225ZM6.1125 14.9001V3.2251H8.9875V14.9001H6.1125ZM11 14.9001V6.2251H13.875V14.9001H11ZM15.9 14.9001V0.225098H18.775V14.9001H15.9Z"
    //         fill="#474747"
    //       />
    //     </svg>
    //   ),
    //   onClick: () => {
    //     const section = document.getElementById('residentPaymentHistory');
    //     section?.scrollIntoView({ behavior: 'smooth' });
    //   },
    // },
    {
      label: 'Archive Resident',
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 15L13 11L11.6 9.6L10 11.2V7H8V11.2L6.4 9.6L5 11L9 15ZM2 5V16H16V5H2ZM2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V3.525C0 3.29167 0.0375 3.06667 0.1125 2.85C0.1875 2.63333 0.3 2.43333 0.45 2.25L1.7 0.725C1.88333 0.491667 2.1125 0.3125 2.3875 0.1875C2.6625 0.0625 2.95 0 3.25 0H14.75C15.05 0 15.3375 0.0625 15.6125 0.1875C15.8875 0.3125 16.1167 0.491667 16.3 0.725L17.55 2.25C17.7 2.43333 17.8125 2.63333 17.8875 2.85C17.9625 3.06667 18 3.29167 18 3.525V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM2.4 3H15.6L14.75 2H3.25L2.4 3Z"
            fill="#474747"
          />
        </svg>
      ),
      onClick: () => {
        setIsArchiveModalOpen(true);
        console.log('Archiving resident...');
      },
    },
    {
      label: 'Delete Resident',
      icon: (
        <svg
          width="16"
          height="18"
          viewBox="0 0 16 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
            fill="#E03A3D"
          />
        </svg>
      ),
      onClick: () => {
        console.log('Deleting resident...');
        setIsDeleteModalOpen(true);
      },
      danger: true,
    },
  ];

  const { residentId } = useParams({ from: '/houses/residents/$residentId' });
  const { data: resident, isLoading, error } = useResidentDetails(residentId!);
  // const { mutate: deleteResident, isPending } = useDeleteResident();



  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Failed to load resident details.</div>;

  return (
    <div className="min-h-screen bg-background w-full px-0 sm:px-6 lg:px-8">
      {/* Header  */}
      <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 mb-4 lg:mb-0">
        <div className="text-center lg:text-start w-full lg:w-[80%]">
          <h3 className="text-xl md:text-3xl font-bold mb-2 text-text-primary">
            Resident details
          </h3>
          <p className="text-gray-600 text-base md:text-xl text-text-secondary">
            House management / Manage residents / Resident details
          </p>
        </div>
        <Button
          variant="primary"
          size="lg"
          leftIcon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 1V7M7 7V13M7 7H13M7 7L1 7"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          onClick={() => setAddResidentModalOpen(true)}
        >
          Add residents
        </Button>
      </div>

      <div className="">
        <div className="grid grid-col-2 xl:grid-cols-3 gap-8 mb-4 md:mb-8">
          <div className="col-span-1">
            <div className="w-full md:w-[50%] xl:w-full mx-auto">
              <div className="w-full rounded-tr-3xl rounded-tl-3xl overflow-hidden mb-4 h-80">
                <img
                  src={resident?.avatar || "/assets/images/resident-profile-placeholder.png"}
                  className="w-full h-full object-cover"
                  alt={resident?.fullname || "Resident"}
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/resident-profile-placeholder.png";
                  }}
                />
              </div>
              <div className="flex justify-between mb-4">
                <div>
                  <h2 className="text-xl md:text-3xl text-text-primary font-bold mb-2">
                    {resident?.fullname}
                  </h2>
                  <p className="text-base md:text-xl text-text-secondary mb-2">
                    {resident?.house.name}
                  </p>
                  <div className="flex justify-between ">
                    <div className="text-lg md:text-xl text-[#177D3D] mb-2 flex items-center gap-3">
                      <span>{resident?.status}</span>
                      <svg
                        width="10"
                        height="5"
                        viewBox="0 0 10 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M5 5L0 0H10L5 5Z" fill="#177D3D" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* share and menu  */}
                <div className="flex">
                  <div className="flex justify-center p-3 cursor-pointer mr-4">
                    <svg
                      width="18"
                      height="20"
                      viewBox="0 0 18 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 20C14.1667 20 13.4583 19.7083 12.875 19.125C12.2917 18.5417 12 17.8333 12 17C12 16.9 12.025 16.6667 12.075 16.3L5.05 12.2C4.78333 12.45 4.475 12.6458 4.125 12.7875C3.775 12.9292 3.4 13 3 13C2.16667 13 1.45833 12.7083 0.875 12.125C0.291667 11.5417 0 10.8333 0 10C0 9.16667 0.291667 8.45833 0.875 7.875C1.45833 7.29167 2.16667 7 3 7C3.4 7 3.775 7.07083 4.125 7.2125C4.475 7.35417 4.78333 7.55 5.05 7.8L12.075 3.7C12.0417 3.58333 12.0208 3.47083 12.0125 3.3625C12.0042 3.25417 12 3.13333 12 3C12 2.16667 12.2917 1.45833 12.875 0.875C13.4583 0.291667 14.1667 0 15 0C15.8333 0 16.5417 0.291667 17.125 0.875C17.7083 1.45833 18 2.16667 18 3C18 3.83333 17.7083 4.54167 17.125 5.125C16.5417 5.70833 15.8333 6 15 6C14.6 6 14.225 5.92917 13.875 5.7875C13.525 5.64583 13.2167 5.45 12.95 5.2L5.925 9.3C5.95833 9.41667 5.97917 9.52917 5.9875 9.6375C5.99583 9.74583 6 9.86667 6 10C6 10.1333 5.99583 10.2542 5.9875 10.3625C5.97917 10.4708 5.95833 10.5833 5.925 10.7L12.95 14.8C13.2167 14.55 13.525 14.3542 13.875 14.2125C14.225 14.0708 14.6 14 15 14C15.8333 14 16.5417 14.2917 17.125 14.875C17.7083 15.4583 18 16.1667 18 17C18 17.8333 17.7083 18.5417 17.125 19.125C16.5417 19.7083 15.8333 20 15 20ZM15 18C15.2833 18 15.5208 17.9042 15.7125 17.7125C15.9042 17.5208 16 17.2833 16 17C16 16.7167 15.9042 16.4792 15.7125 16.2875C15.5208 16.0958 15.2833 16 15 16C14.7167 16 14.4792 16.0958 14.2875 16.2875C14.0958 16.4792 14 16.7167 14 17C14 17.2833 14.0958 17.5208 14.2875 17.7125C14.4792 17.9042 14.7167 18 15 18ZM3 11C3.28333 11 3.52083 10.9042 3.7125 10.7125C3.90417 10.5208 4 10.2833 4 10C4 9.71667 3.90417 9.47917 3.7125 9.2875C3.52083 9.09583 3.28333 9 3 9C2.71667 9 2.47917 9.09583 2.2875 9.2875C2.09583 9.47917 2 9.71667 2 10C2 10.2833 2.09583 10.5208 2.2875 10.7125C2.47917 10.9042 2.71667 11 3 11ZM15 4C15.2833 4 15.5208 3.90417 15.7125 3.7125C15.9042 3.52083 16 3.28333 16 3C16 2.71667 15.9042 2.47917 15.7125 2.2875C15.5208 2.09583 15.2833 2 15 2C14.7167 2 14.4792 2.09583 14.2875 2.2875C14.0958 2.47917 14 2.71667 14 3C14 3.28333 14.0958 3.52083 14.2875 3.7125C14.4792 3.90417 14.7167 4 15 4Z"
                        fill="#474747"
                      />
                    </svg>
                  </div>
                  <button
                    className="flex justify-center p-3 cursor-pointer"
                    onClick={() => setShowMenu(!showMenu)}
                  >
                    <svg
                      width="4"
                      height="16"
                      viewBox="0 0 4 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14C0 13.45 0.195833 12.9792 0.5875 12.5875C0.979167 12.1958 1.45 12 2 12C2.55 12 3.02083 12.1958 3.4125 12.5875C3.80417 12.9792 4 13.45 4 14C4 14.55 3.80417 15.0208 3.4125 15.4125C3.02083 15.8042 2.55 16 2 16ZM2 10C1.45 10 0.979167 9.80417 0.5875 9.4125C0.195833 9.02083 0 8.55 0 8C0 7.45 0.195833 6.97917 0.5875 6.5875C0.979167 6.19583 1.45 6 2 6C2.55 6 3.02083 6.19583 3.4125 6.5875C3.80417 6.97917 4 7.45 4 8C4 8.55 3.80417 9.02083 3.4125 9.4125C3.02083 9.80417 2.55 10 2 10ZM2 4C1.45 4 0.979167 3.80417 0.5875 3.4125C0.195833 3.02083 0 2.55 0 2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0C2.55 0 3.02083 0.195833 3.4125 0.5875C3.80417 0.979167 4 1.45 4 2C4 2.55 3.80417 3.02083 3.4125 3.4125C3.02083 3.80417 2.55 4 2 4Z"
                        fill="#474747"
                      />
                    </svg>
                  </button>

                  {/* menu  */}
                  <OptionMenu
                    menuItems={residentMenuItems}
                    isOpen={showMenu}
                    onClose={() => setShowMenu(false)}
                  />
                </div>
              </div>

              <div>
                <Button
                  className="w-full bg-white hover:!bg-[#E03A3D] !border !border-[#E03A3D] !text-[#E03A3D] hover:!text-white text-bold !text-lg cursor-pointer"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  Delete Resident
                </Button>
              </div>
            </div>
          </div>

          <div className="col-span-1 xl:col-span-2">
            <div className="grid grid-cols-2 gap-4 mb-4 md:mb-8">
              <div className="p-4 card">
                <p className="text-text-primary text-xl md:text-[28px] font-bold mb-2 ">
                  12-A
                </p>
                <p className="text-text-secondary text-base md:text-xl">
                  {resident?.house.name}
                </p>
              </div>
              <div className="p-4 card">
                <p className="text-text-primary text-xl md:text-[28px] font-bold mb-2 ">
                  $ {resident?.monthlyFees}
                </p>
                <p className="text-text-secondary text-base md:text-xl">
                  Current program fees
                </p>
              </div>
            </div>

            {/* contact  */}
            <div className="mb-4 md:mb-8">
              <h4 className="text-text-primary text-lg md:text-xl font-bold mb-4 ">
                Contact details
              </h4>

              <div className="py-2 flex items-center gap-3 mb-2">
                <div>
                  <img src="/assets/icons/phone.svg" alt="" />
                </div>
                <div>
                  <p className="text-text-secondary mb-1">Phone number</p>
                  <p className="text-text-primary text-base md:text-xl">{resident?.phone}</p>
                </div>
              </div>

              <div className="py-2 flex items-center justify-between gap-3 mb-2">
                <div className="py-2 flex items-center gap-3">
                  <div>
                    <img src="/assets/icons/mail.svg" alt="" />
                  </div>
                  <div>
                    <p className="text-text-secondary mb-1">Email address</p>
                    <p className="text-text-primary text-base md:text-xl">
                      {resident?.email}
                    </p>
                  </div>
                </div>
                <div>
                  {/* Copy to clipboard */}
                  <CopyToClipboard text={resident?.email || ''} label="Email" />
                </div>
              </div>

              <div className="py-2 flex items-center justify-between gap-3 mb-2">
                <div className="py-2 flex items-center gap-3">
                  <div>
                    <img src="/assets/icons/mail.svg" alt="" />
                  </div>
                  <div>
                    <p className="text-text-secondary mb-1">ID number</p>
                    <p className="text-text-primary text-base md:text-xl">{resident?.id}</p>
                  </div>
                </div>
                <div>
                  {/* Copy to clipboard */}
                  <CopyToClipboard text={resident?.id || ''} label="ID" />
                </div>
              </div>

              <div className="py-2 flex items-center gap-3">
                <div>
                  <img src="/assets/icons/calender.svg" alt="" />
                </div>
                <div>
                  <p className="text-text-secondary mb-1">Move in date</p>
                  <p className="text-text-primary text-base md:text-xl">
                    {formattedDate(resident?.moveInDate)}
                  </p>
                </div>
              </div>

              <div className="py-2 flex items-center gap-3 mb-2">
                <div>
                  <img
                    src="/assets/icons/emergency-phone.svg"
                    alt="Emergency Contact"
                  />
                </div>
                <div>
                  <p className="text-text-secondary mb-1">Emergency contact</p>
                  <p className="text-text-primary text-base md:text-xl">
                    {resident?.phone ?? 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h4 className="text-text-primary text-lg md:text-xl font-bold mb-4 ">
                Permissions
              </h4>
              {permissionList.map((perm) => (
                <div key={perm.key} className="p-2 md:p-4 border-b border-gray-100">
                  <Toggle
                    label={perm.label}
                    value={perm.defaultValue}
                    onChange={perm.handler}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment history */}
        <div id="residentPaymentHistory">
          <h4 className="text-text-primary text-lg md:text-xl font-bold mb-2">
            Payment history
          </h4>

          <div className="overflow-x-auto">
            <ResidentPaymentHistoryTable data={resident?.payments || []} />
          </div>
        </div>
      </div>

      <AddResidentModal
        isOpen={isAddResidentModalOpen}
        onClose={() => setAddResidentModalOpen(false)}
      />

      {/* Delete Modal */}
      <DeleteResidentModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() => {
          // deleteResident(id);
          setIsDeleteModalOpen(false);
        }}
        resident={{
          id: resident?.id || '',
          name: resident?.fullname || '',
        }}
      />

      <ArchieveResidentModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onConfirm={() => {
          setIsArchiveModalOpen(false);
        }}
        resident={{ id: resident?.id || '', name: resident?.fullname || '' }}
      />
    </div>
  );
};

export const ResidentDetailsPage = () => {
  return (
    <ResidentsErrorBoundary onRetry={() => window.location.reload()}>
      <ResidentDetailsPageContent />
    </ResidentsErrorBoundary>
  );
};
